import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/SEOHead';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export const CookiePolicy = () => {
  const { t } = useTranslation();

  return (
    <>
      <SEOHead />
      
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-newsletter-primary transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('cookiePolicy.backToHome')}
          </Link>

          <div className="bg-card border rounded-lg p-8">
            <h1 className="text-3xl font-bold mb-6 text-newsletter-primary">
              {t('cookiePolicy.title')}
            </h1>

            <div className="prose prose-invert max-w-none space-y-6">
              <section>
                <h2 className="text-xl font-semibold mb-3 text-foreground">
                  {t('cookiePolicy.whatAreCookies.title')}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {t('cookiePolicy.whatAreCookies.description')}
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3 text-foreground">
                  {t('cookiePolicy.typesOfCookies.title')}
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium mb-2 text-newsletter-accent">
                      {t('cookiePolicy.typesOfCookies.essential.title')}
                    </h3>
                    <p className="text-muted-foreground mb-2">
                      {t('cookiePolicy.typesOfCookies.essential.description')}
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li>{t('cookiePolicy.typesOfCookies.essential.examples.session')}</li>
                      <li>{t('cookiePolicy.typesOfCookies.essential.examples.language')}</li>
                      <li>{t('cookiePolicy.typesOfCookies.essential.examples.interface')}</li>
                      <li>{t('cookiePolicy.typesOfCookies.essential.examples.stripe')}</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium mb-2 text-newsletter-accent">
                      {t('cookiePolicy.typesOfCookies.analytical.title')}
                    </h3>
                    <p className="text-muted-foreground mb-2">
                      {t('cookiePolicy.typesOfCookies.analytical.description')}
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li>{t('cookiePolicy.typesOfCookies.analytical.examples.googleAnalytics')}</li>
                      <li>{t('cookiePolicy.typesOfCookies.analytical.examples.hotjar')}</li>
                      <li>{t('cookiePolicy.typesOfCookies.analytical.examples.mixpanel')}</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium mb-2 text-newsletter-accent">
                      {t('cookiePolicy.typesOfCookies.marketing.title')}
                    </h3>
                    <p className="text-muted-foreground mb-2">
                      {t('cookiePolicy.typesOfCookies.marketing.description')}
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li>{t('cookiePolicy.typesOfCookies.marketing.examples.googleAds')}</li>
                      <li>{t('cookiePolicy.typesOfCookies.marketing.examples.conversions')}</li>
                      <li>{t('cookiePolicy.typesOfCookies.marketing.examples.remarketing')}</li>
                    </ul>
                  </div>

                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3 text-foreground">
                  {t('cookiePolicy.duration.title')}
                </h2>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                  <li>{t('cookiePolicy.duration.session')}</li>
                  <li>{t('cookiePolicy.duration.persistent')}</li>
                  <li>{t('cookiePolicy.duration.analytical')}</li>
                  <li>{t('cookiePolicy.duration.marketing')}</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3 text-foreground">
                  {t('cookiePolicy.rights.title')}
                </h2>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                  <li>{t('cookiePolicy.rights.accept')}</li>
                  <li>{t('cookiePolicy.rights.change')}</li>
                  <li>{t('cookiePolicy.rights.delete')}</li>
                  <li>{t('cookiePolicy.rights.know')}</li>
                  <li>{t('cookiePolicy.rights.withdraw')}</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3 text-foreground">
                  {t('cookiePolicy.management.title')}
                </h2>
                <div className="space-y-3">
                  <div>
                    <h3 className="font-medium text-foreground">
                      {t('cookiePolicy.management.onSite.title')}
                    </h3>
                    <p className="text-muted-foreground">
                      {t('cookiePolicy.management.onSite.description')}
                    </p>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">
                      {t('cookiePolicy.management.browser.title')}
                    </h3>
                    <p className="text-muted-foreground">
                      {t('cookiePolicy.management.browser.description')}
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3 text-foreground">
                  {t('cookiePolicy.thirdParty.title')}
                </h2>
                <p className="text-muted-foreground mb-2">
                  {t('cookiePolicy.thirdParty.description')}
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                  <li>Google (Analytics, Ads)</li>
                  <li>Hotjar</li>
                  <li>Mixpanel</li>
                  <li>Stripe</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3 text-foreground">
                  {t('cookiePolicy.dataTransfer.title')}
                </h2>
                <p className="text-muted-foreground">
                  {t('cookiePolicy.dataTransfer.description')}
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3 text-foreground">
                  {t('cookiePolicy.updates.title')}
                </h2>
                <p className="text-muted-foreground">
                  {t('cookiePolicy.updates.description')}
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3 text-foreground">
                  {t('cookiePolicy.contact.title')}
                </h2>
                <div className="bg-muted/20 border border-newsletter-primary/20 rounded-lg p-4">
                  <p className="text-muted-foreground mb-2">
                    {t('cookiePolicy.contact.description')}
                  </p>
                  <p className="font-medium text-newsletter-primary">
                    📧 {t('cookiePolicy.contact.email')}: privacy@newsforyou.ai
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    📞 {t('cookiePolicy.contact.response')}
                  </p>
                </div>
              </section>

              <div className="border-t border-border pt-4 mt-8">
                <p className="text-sm text-muted-foreground">
                  <strong>{t('cookiePolicy.lastUpdate')}:</strong> {new Date().toLocaleDateString()}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  <strong>{t('cookiePolicy.legalBasis')}:</strong> {t('cookiePolicy.legalBasisText')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};